<template>
  <page-wrapper :title="title" :has-pad="true">
    <div v-if="submission" class="wrapper row">
      <div class="col-md-3 member">
        <p>ชื่อ</p>
        <h6>{{ submissionPresenter.name }}</h6>
        <p>E-mail</p>
        <h6>{{ submissionPresenter.email }}</h6>
        <p>เบอร์โทรศัพท์</p>
        <h6>{{ submissionPresenter.phoneNumber }}</h6>
        <p>บ้านเลขที่</p>
        <h6>{{ submissionPresenter.home.addressNumber }}</h6>
        <p>โครงการ</p>
        <h6>{{ submissionPresenter.home.project }}</h6>
      </div>
      <div class="col-md-4 info">
        <p>ระยะเวลาที่ซื้อบ้าน</p>
        <h6>{{ loanPresenter.timePeriodFromPurchased }}</h6>
        <p>ธนาคารที่ผ่อนปัจจุบัน</p>
        <h6>{{ loanPresenter.bankName }}</h6>
        <p>มูลค่าบ้าน</p>
        <h6>{{ loanPresenter.purchasedPrice | money }} บาท</h6>
        <p>วงเงินกู้บ้านเดิม</p>
        <h6>{{ loanPresenter.total | money }} บาท</h6>
        <p>ยอดผ่อนต่อเดือน</p>
        <h6>{{ loanPresenter.monthlyPayment | money }} บาท</h6>
        <p>จำนวนปีที่ผ่อน</p>
        <h6>{{ loanPresenter.paymentPeriod }}</h6>
        <p>วงเงินกู้คงเหลือ</p>
        <h6>{{ loanPresenter.balance | money }} บาท</h6>
        <p>อัตราดอกเบี้ยปีที่ 1-3</p>
        <h6>{{ loanPresenter.interestRateInThreeYears }}</h6>
        <p>อัตราดอกเบี้ยปีที่ 3 เป็นต้นไป</p>
        <h6>{{ loanPresenter.interestRateAfterThreeYears }}</h6>
        <p>จุดประสงค์ของการรีไฟแนนซ์</p>
        <h6
          v-for="item in loanPresenter.objectives"
          :key="item.id"
          class="objective"
        >
          {{ item.title }}
        </h6>
      </div>
      <div class="col-md-4 info">
        <p>สถาบันการเงินที่ยื่นคำขอ</p>
        <h6>{{ financialInstitutionPresenter.name }}</h6>
        <hr />
        <div v-if="showUpdateStatus" class="update-status">
          <p>เปลี่ยนสถานะ</p>
          <refinance-status-select
            v-model="updatedStatus"
            :hide-item="handleHiddenItem"
            class="status-select"
          ></refinance-status-select>
          <div v-if="showNotInterestReason" class="mb-4">
            <label class="label">เหตุผลที่ไม่สนใจ</label>
            <el-input v-model="notInterestedReason"></el-input>
          </div>
          <div class="text-end">
            <sc-button
              class="status-button"
              variant="primary"
              @click="handleClickUpdateStatus"
              >บันทึก</sc-button
            >
          </div>
        </div>
        <div v-else>
          <p>สถานะ</p>
          <h4 :class="currentStatusCss" class="current-status">
            {{ currentStatusText }}
          </h4>
          <p>เหตุผลที่ไม่สนใจ</p>
          <p>{{ notInterestedReason }}</p>
        </div>
        <hr />
        <div v-for="(status, index) in statusPresenter" :key="index">
          <p>{{ status.label }}</p>
          <h6>{{ status.date | date }}</h6>
        </div>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
import Vue from "vue"
import { isNil } from "ramda"
import { DateTime } from "luxon"
import { Input as ElInput } from "element-ui"

import { baanRueJaiClient } from "@/dependencies"
import PageWrapper from "@/lib/components/PageWrapper"
import ScButton from "@/lib/components/ScButton"
import RefinanceStatusSelect from "../components/RefinanceStatusSelect"
import { RefinanceStatus, RefinanceStatusEnum } from "../RefinanceStatus"

export default Vue.extend({
  components: {
    PageWrapper,
    ScButton,
    RefinanceStatusSelect,
    ElInput,
  },
  data() {
    const title = "รายละเอียดรีไฟแนนซ์"
    const submission = null
    const updatedStatus = null
    const notInterestedReason = null

    return { title, submission, updatedStatus, notInterestedReason }
  },
  computed: {
    disabledNotInterestReason() {
      return (
        this.currentStatus === RefinanceStatusEnum.NOT_INTERESTED &&
        this.submission.not_interested_reason
      )
    },
    showNotInterestReason() {
      return this.updatedStatus === RefinanceStatusEnum.NOT_INTERESTED
    },
    submissionPresenter() {
      const { form } = this.submission
      const { user } = form

      const { name } = user
      const { email } = user
      const phoneNumber = user.phone_number
      const home = {
        addressNumber: form.home.address_number,
        project: form.home.project.name,
      }

      return {
        name,
        email,
        phoneNumber,
        home,
      }
    },
    loanPresenter() {
      const { home, loan, objectives } = this.submission.form

      const timePeriodFromPurchased = this.formatDate(
        home.time_period_from_purchased,
      )
      const bankName = loan.current_bank
      const { total } = loan
      const monthlyPayment = loan.monthly_payment
      const paymentPeriod = this.formatDate(loan.time_period_of_paid)
      const { balance } = loan
      const interestRateInThreeYears = loan.interest_rate_in_three_year
      const interestRateAfterThreeYears = loan.interest_rate_after_third_year
      const purchasedPrice = home.purchase_price

      return {
        timePeriodFromPurchased,
        bankName,
        total,
        monthlyPayment,
        paymentPeriod,
        balance,
        interestRateInThreeYears,
        interestRateAfterThreeYears,
        purchasedPrice,
        objectives,
      }
    },
    financialInstitutionPresenter() {
      return { name: this.submission.financial_institution.name }
    },
    statusPresenter() {
      const labels = {
        created_at: "ส่งคำขอเมื่อ",
        feedback_at: "เจ้าหน้าที่ติดต่อกลับเมื่อ",
        interest_compared_at: "ลูกค้าขอพิจารณาเมื่อ",
        received_document_at: "ได้รับเอกสารเมื่อ",
        pre_approved_at: "อนุมัติเบื้องต้นเมื่อ",
        appraised_at: "ประเมินราคาเมื่อ",
        final_approved_at: "ได้รับการอนุมัติเมื่อ",
        rejected_at: "ไม่ผ่านการอนุมัติ",
        not_interested_at: "ไม่สนใจรับบริการ",
      }

      return Object.keys(labels)
        .map((key) => ({ label: labels[key], date: this.submission[key] }))
        .filter(({ date }) => !!date)
    },
    currentStatus() {
      return this.submission.status
    },
    showUpdateStatus() {
      return !this.isRejected && !this.isCompleted && !this.isNotInterested
    },
    isRejected() {
      return !isNil(this.submission.rejected_at)
    },
    isCompleted() {
      return !isNil(this.submission.final_approved_at)
    },
    isNotInterested() {
      return !isNil(this.submission.not_interested_at)
    },
    statusWeight() {
      return Object.keys(RefinanceStatus)
    },
    currentStatusText() {
      return RefinanceStatus[this.currentStatus]
    },
    currentStatusCss() {
      return { completed: this.isCompleted, rejected: this.isRejected }
    },
  },
  created() {
    this.setup()
  },
  methods: {
    async setup() {
      await this.setupData()
    },
    async setupData() {
      await this.setupSubmission()
    },
    async setupSubmission() {
      const data = await this.fetchData()

      this.submission = data.refinance_submission
      this.updatedStatus = this.submission.status
      this.notInterestedReason = this.submission.not_interested_reason
    },
    async fetchData() {
      const response =
        await baanRueJaiClient.admin.homes.refinance.getSubmission(
          this.$route.params.id,
        )

      return response.data.result
    },

    getCurrentTime() {
      return DateTime.utc()
    },
    formatDate(dateInput) {
      const year = parseInt(dateInput.slice(0, 2), 10)
      const month = parseInt(dateInput.slice(2), 10)

      const formattedDate = year
        ? `${year} ปี ${month} เดือน`
        : `${month} เดือน`

      return formattedDate
    },
    async handleClickUpdateStatus() {
      if (this.currentStatus === this.updatedStatus) {
        return
      }

      const max = this.statusWeight.findIndex((s) => s === this.updatedStatus)
      const min = this.statusWeight.findIndex((s) => s === this.currentStatus)

      const map = {
        AWAITING_INTEREST_COMPARISON: "feedback_at",
        AWAITING_DOCUMENT: "interest_compared_at",
        AWAITING_PRE_APPROVE: "received_document_at",
        AWAITING_APPRAISE: "pre_approved_at",
        AWAITING_FINAL_APPROVE: "appraised_at",
        COMPLETED: "final_approved_at",
        REJECTED: "rejected_at",
        NOT_INTERESTED: "not_interested_at",
      }

      const isRejected =
        this.updatedStatus === "REJECTED" ||
        this.updatedStatus === "NOT_INTERESTED"
      const keys = isRejected
        ? [map[this.updatedStatus]]
        : this.statusWeight
            .filter((_, w) => w > min && w <= max)
            .map((key) => map[key])

      // eslint-disable-next-line no-alert
      if (!window.confirm("ยืนยันการเปลี่ยนสถานะ")) {
        return
      }

      await this.updateStatus(keys)
      await this.setupData()
    },
    async updateStatus(statusList) {
      const { id } = this.$route.params
      const now = DateTime.utc().toISO()
      const params = statusList.reduce(
        (all, status) => ({
          ...all,
          [status]: now,
        }),
        { not_interested_reason: this.notInterestedReason },
      )

      await baanRueJaiClient.admin.homes.refinance.put(id, params)
    },
    handleHiddenItem(status) {
      const current = this.statusWeight.findIndex(
        (s) => s === this.currentStatus,
      )
      const target = this.statusWeight.findIndex((s) => s === status)

      return target < current
    },
  },
})
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

$break-small: 600px;
$break-large: 1000px;

.wrapper {
  padding: $space 0;
}

.member h6,
.info h6 {
  margin-bottom: 20px;
}

.info {
  @media screen and (max-width: $break-small) {
    padding-top: 20px;
    border-top: $border;
  }

  @media screen and (min-width: $break-large) {
    border-left: $border;
  }

  > .objective {
    margin-bottom: 10px;
  }
}

.update-status {
  margin-bottom: 1rem;

  > .status-select {
    width: 100%;
    margin-bottom: 0.5rem;
  }
}

.current-status {
  &.completed {
    color: $success-color;
  }

  &.rejected {
    color: $danger-color;
  }
}
</style>
